// sso.js - SSO bridge helpers for Customer Panel -> Test Portal (CakePHP)

export const TEST_PORTAL_BASE = process.env.REACT_APP_TEST_PORTAL_URL || "https://test.pyqpedia.com";
export const SSO_BRIDGE_PATH = "/sso/login";
export const SSO_CLEAR_PATH = "/sso/clear";

/**
 * Get stored token from sessionStorage.studentSession
 */
export function getStoredToken() {
  try {
    const session = sessionStorage.getItem("studentSession");
    if (!session) return null;
    const data = JSON.parse(session);
    return data?.token || null;
  } catch {
    return null;
  }
}

/**
 * Build SSO bridge URL for test portal.
 * @param {string} token - plain text token from Laravel login
 * @param {string} next - path on test portal to redirect after SSO (default "/")
 */
export function buildSsoUrl(token, next = "/") {
  if (!token) return `${TEST_PORTAL_BASE}/`;
  const url = `${TEST_PORTAL_BASE}${SSO_BRIDGE_PATH}?token=${encodeURIComponent(token)}&next=${encodeURIComponent(next)}`;
  return url;
}

/**
 * Build URL using stored token (fallback to direct portal URL)
 */
export function getTestPortalUrl(next = "/") {
  const token = getStoredToken();
  if (!token) return `${TEST_PORTAL_BASE}${next}`;
  return buildSsoUrl(token, next);
}

/**
 * Background sync sso_token cookie on test portal via hidden iframe.
 * This makes later navigation to test portal auto-logged in via AppController::apiTokenSsoBridge().
 * Uses sso_url returned from Laravel if provided, else builds one.
 * @param {string} tokenOrUrl - either plain token or full sso_url
 */
export function syncSsoCookie(tokenOrUrl) {
  if (!tokenOrUrl) return;
  let ssoUrl;
  if (tokenOrUrl.startsWith("http")) {
    ssoUrl = tokenOrUrl;
  } else {
    ssoUrl = buildSsoUrl(tokenOrUrl, "/");
  }
  try {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = ssoUrl;
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
    // Clean up after 8s (bridge redirects to next, still sets cookie)
    setTimeout(() => {
      try {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      } catch {}
    }, 8000);
    // Also try image ping fallback (some browsers block iframe cookies without user gesture)
    // We keep iframe as primary, image as no-op fallback not needed.
  } catch (e) {
    // Fallback: use image request (will still hit bridge but not store HttpOnly correctly)
    try {
      const img = new Image();
      img.src = ssoUrl;
    } catch {}
  }
}

/**
 * Build the logout URL: navigates to test-portal /sso/clear which destroys the
 * Cake Student session + deletes the sso_token cookie, then redirects to nextUrl.
 * This is synchronous (top-level navigation) so it is NOT blocked by third-party
 * iframe/cookie protections — unlike a hidden iframe.
 * @param {string} nextUrl - where to land after logout (default test portal "/")
 */
export function getLogoutUrl(nextUrl = "/") {
  return `${TEST_PORTAL_BASE}${SSO_CLEAR_PATH}?next=${encodeURIComponent(nextUrl)}`;
}

/**
 * Clear SSO cookie on test portal via hidden iframe to /sso/clear.
 * Best-effort only; use redirectLogout() for a reliable logout.
 */
export function clearSsoCookie() {
  try {
    const clearUrl = `${TEST_PORTAL_BASE}${SSO_CLEAR_PATH}`;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = clearUrl;
    document.body.appendChild(iframe);
    setTimeout(() => {
      try {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      } catch {}
    }, 5000);
  } catch {}
}
